// Supplement reminders — schedules a daily iOS local notification at the
// user's preferred time (default 18:30) and exposes the data model that
// the in-app banner uses to detect which of Yusuf's daily-5 supplements
// haven't been logged yet today.
//
// The five quick-log tiles in CalorieCounter inline their food bases;
// the matchers below detect those logged entries by name/brand regex.

import { LocalNotifications } from '@capacitor/local-notifications';

export const DEFAULT_TIME = '18:30';
const TIME_PREF_KEY = 'sirat.hayah.supplementReminderTime';
const DISMISS_KEY   = 'sirat.hayah.supplementReminderDismissed';
const NOTIF_ID      = 9001;

// Canonical defs — the banner uses these for chips and one-tap re-logging.
export const SUPPLEMENT_DEFS = [
  {
    id: 'creatine',
    emoji: '💪',
    label: 'Creatine',
    color: '#6b9a6e',
    match: (e) => /creatine|creapure/i.test(`${e?.name || ''} ${e?.brand || ''}`),
    base: { brand: "Foundation", name: "Creapure Creatine Monohydrate", category: "Supplements", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, servingG: 5, servingLabel: "1 scoop (5g)", halal: true },
    servingG: 5,
    servingLabel: "1 scoop (5g)",
    toast: "💪 Creatine logged · 5g",
  },
  {
    id: 'omega3',
    emoji: '🐟',
    label: 'Omega-3',
    color: '#4a90d9',
    match: (e) => /omega[- ]?3|fish oil|epa[- ]?dha/i.test(`${e?.name || ''} ${e?.brand || ''}`),
    base: { brand: "Biogen", name: "Biogen Supreme Omega-3", category: "Supplements", calories: 18, protein: 0, carbs: 0, fat: 2, fiber: 0, sugar: 0, sodium: 0, servingG: 2, servingLabel: "2 softgels", halal: true },
    servingG: 2,
    servingLabel: "2 softgels",
    toast: "🐟 Omega-3 logged · 18 kcal",
  },
  {
    id: 'collagen',
    emoji: '🦴',
    label: 'Collagen',
    color: '#d36b8a',
    match: (e) => /collagen/i.test(`${e?.name || ''} ${e?.brand || ''}`),
    base: { brand: "Biogen", name: "Biogen Collagen Capsules", category: "Supplements", calories: 6, protein: 1.5, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, servingG: 1.5, servingLabel: "3 capsules", halal: true },
    servingG: 1.5,
    servingLabel: "3 capsules",
    toast: "🦴 Collagen logged · 3 caps",
  },
  {
    id: 'magnesium',
    emoji: '✨',
    label: 'Magnesium',
    color: '#5fb8a8',
    match: (e) => /magnesium/i.test(`${e?.name || ''} ${e?.brand || ''}`),
    base: { brand: "Biogen", name: "Biogen Magnesium Glycinate", category: "Supplements", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, servingG: 1, servingLabel: "1 tablet", halal: true },
    servingG: 1,
    servingLabel: "1 tablet",
    toast: "✨ Magnesium logged · 1 tab",
  },
  {
    id: 'psyllium',
    emoji: '🌾',
    label: 'Psyllium',
    color: '#7c9a4a',
    match: (e) => /psyllium/i.test(`${e?.name || ''} ${e?.brand || ''}`),
    base: { brand: "Lifestyle Food", name: "Psyllium Husk", category: "Supplements", calories: 40, protein: 0, carbs: 10, fat: 0, fiber: 10, sugar: 0, sodium: 0, servingG: 12, servingLabel: "2 heaped tsp (12g)", halal: true },
    servingG: 12,
    servingLabel: "2 heaped tsp (12g)",
    toast: "🌾 Psyllium logged · 10g fibre",
  },
  {
    id: 'revive',
    emoji: '⚡',
    label: 'Revive',
    color: '#e0567a',
    match: (e) => /revive|electrolyte/i.test(`${e?.name || ''} ${e?.brand || ''}`),
    base: { brand: "Revive", name: "Revive Raspberry", category: "Supplements", calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 1000, servingG: 6, servingLabel: "1 sachet", halal: true },
    servingG: 6,
    servingLabel: "1 sachet",
    toast: "⚡ Revive logged",
  },
];

export function getReminderTime() {
  try { return localStorage.getItem(TIME_PREF_KEY) || DEFAULT_TIME; } catch { return DEFAULT_TIME; }
}
export function setReminderTime(t) {
  try { localStorage.setItem(TIME_PREF_KEY, t); } catch {}
  return scheduleDailyReminder();
}

export function getMissingToday(calorieLog, todayKey) {
  const supps = (calorieLog?.[todayKey]?.meals?.supplements) || [];
  return SUPPLEMENT_DEFS.filter(d => !supps.some(e => d.match(e)));
}

export function isPastReminderTime() {
  const t = getReminderTime();
  const [hh, mm] = t.split(':').map(n => parseInt(n, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes() >= hh * 60 + mm;
}

export function isDismissedToday(todayKey) {
  try { return localStorage.getItem(DISMISS_KEY) === todayKey; } catch { return false; }
}
export function dismissToday(todayKey) {
  try { localStorage.setItem(DISMISS_KEY, todayKey); } catch {}
}

function isNative() {
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

export async function ensurePermission() {
  if (!isNative()) return false;
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status?.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req?.display === 'granted';
  } catch { return false; }
}

export async function scheduleDailyReminder() {
  if (!isNative()) return false;
  try {
    const t = getReminderTime();
    const [hh, mm] = t.split(':').map(n => parseInt(n, 10));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
    try { await LocalNotifications.cancel({ notifications: [{ id: NOTIF_ID }] }); } catch {}
    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIF_ID,
        title: '🌙 Supplement check',
        body: "Time to log today's supplements",
        schedule: { on: { hour: hh, minute: mm }, allowWhileIdle: true },
      }],
    });
    return true;
  } catch { return false; }
}

// Cancel the daily supplement-check notification (Yusuf opted out).
export async function cancelSupplementReminder() {
  if (!isNative()) return false;
  try { await LocalNotifications.cancel({ notifications: [{ id: NOTIF_ID }] }); return true; }
  catch { return false; }
}

export async function initSupplementReminders() {
  if (!isNative()) return;
  // Supplement reminder disabled per user request — cancel any previously
  // scheduled repeating notification so it stops firing.
  await cancelSupplementReminder();
}
